// ============================================================================
// PHI POLICY SETTINGS VIEW (BLOCK 13 - Platform Admin Only)
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Shield, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { getPhiPolicy, savePhiPolicy, getTenants } from '../core/index.js';
import { useAuth } from '../auth/AuthContext.jsx';

const PhiPolicySettingsView = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [formValues, setFormValues] = useState({
    pilotModeEnabled: true,
    textAreaPolicy: 'allow_warn',
    attestationRequired: true,
    scanEnabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Load tenants on mount
  useEffect(() => {
    const loadTenants = async () => {
      setLoading(true);
      try {
        const tenantsData = await getTenants();
        setTenants(tenantsData || {});

        // Auto-select first tenant if available
        const tenantIds = Object.keys(tenantsData || {});
        if (tenantIds.length > 0) {
          setSelectedTenantId(tenantIds[0]);
        }
      } catch (err) {
        console.error('[PhiPolicySettings] Failed to load tenants:', err);
        setError('Failed to load tenants. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
  }, []);

  // Load policy when tenant changes
  useEffect(() => {
    if (!selectedTenantId) {
      setCurrentPolicy(null);
      return;
    }

    const loadPolicy = async () => {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      try {
        console.log('[PhiPolicySettings] Loading policy for tenant:', selectedTenantId);
        const policy = await getPhiPolicy(selectedTenantId);
        console.log('[PhiPolicySettings] Loaded policy:', policy);
        setCurrentPolicy(policy);

        // Update form values with loaded policy
        setFormValues({
          pilotModeEnabled: policy.pilotModeEnabled !== false,
          textAreaPolicy: policy.textAreaPolicy || 'allow_warn',
          attestationRequired: policy.attestationRequired !== false,
          scanEnabled: policy.scanEnabled !== false,
        });
      } catch (err) {
        console.error('[PhiPolicySettings] Failed to load policy:', err);
        setError('Failed to load PHI policy. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadPolicy();
  }, [selectedTenantId]);

  const handleSave = async () => {
    if (!selectedTenantId) {
      setError('Please select a tenant first.');
      return;
    }

    if (!user?.uid) {
      setError('User not authenticated.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      console.log('[PhiPolicySettings] Saving policy for tenant:', selectedTenantId);
      console.log('[PhiPolicySettings] Form values:', formValues);

      await savePhiPolicy(selectedTenantId, user.uid, formValues);

      console.log('[PhiPolicySettings] ✅ Policy saved successfully');
      setSuccessMessage('PHI policy saved successfully!');

      // Reload policy to confirm
      const updatedPolicy = await getPhiPolicy(selectedTenantId);
      setCurrentPolicy(updatedPolicy);
    } catch (err) {
      console.error('[PhiPolicySettings] ❌ Failed to save policy:', err);
      setError(`Failed to save PHI policy: ${safeUserError(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
    setSuccessMessage(null); // Clear success message on edit
  };

  if (loading && !currentPolicy) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tenantList = Object.values(tenants);

  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 mb-4">
          <div className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
          <span className="text-xs font-medium text-violet-400 uppercase">Platform Admin</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-light text-white mb-2">PHI Policy Settings</h2>
        <p className="text-slate-400">Configure pilot mode runtime safety guardrails per tenant</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-400">Error</p>
            <p className="text-sm text-red-300/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-emerald-400">Success</p>
            <p className="text-sm text-emerald-300/80 mt-1">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Tenant Selector */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-6">
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Select Tenant
        </label>
        <select
          value={selectedTenantId}
          onChange={(e) => setSelectedTenantId(e.target.value)}
          disabled={loading || saving}
          className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">-- Select a tenant --</option>
          {tenantList.map((tenant) => {
            const tenantId = tenant.tenantId || tenant.id;
            return (
              <option key={tenantId} value={tenantId}>
                {tenant.name} ({tenantId})
              </option>
            );
          })}
        </select>
        {tenantList.length === 0 && (
          <p className="text-sm text-slate-500 mt-2">No tenants available.</p>
        )}
      </div>

      {/* Policy Form */}
      {currentPolicy && selectedTenantId && (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-violet-400" />
            <h3 className="text-lg font-medium text-white">Policy Configuration</h3>
          </div>

          {/* Policy Version (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Policy Version
            </label>
            <input
              type="text"
              value={currentPolicy.phiPolicyVersion || 'pilot-v2'}
              disabled
              className="w-full px-4 py-2 bg-slate-900/50 border border-slate-700/50 rounded-lg text-slate-500 cursor-not-allowed"
            />
          </div>

          {/* Pilot Mode Enabled Toggle */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formValues.pilotModeEnabled}
                onChange={(e) => handleFormChange('pilotModeEnabled', e.target.checked)}
                disabled={saving}
                className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div>
                <span className="text-sm font-medium text-white">Pilot Mode Enabled</span>
                <p className="text-xs text-slate-500 mt-0.5">
                  Enable pilot mode runtime safety guardrails
                </p>
              </div>
            </label>
          </div>

          {/* Textarea Policy (Radio Buttons) */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Textarea Policy
            </label>
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-900/50 transition-colors">
                <input
                  type="radio"
                  name="textAreaPolicy"
                  value="allow_warn"
                  checked={formValues.textAreaPolicy === 'allow_warn'}
                  onChange={(e) => handleFormChange('textAreaPolicy', e.target.value)}
                  disabled={saving}
                  className="mt-0.5 w-4 h-4 border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div>
                  <span className="text-sm font-medium text-white">Allow with Warning</span>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Show inline warnings when PHI is detected, but allow submission with attestation
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-900/50 transition-colors">
                <input
                  type="radio"
                  name="textAreaPolicy"
                  value="block_on_detection"
                  checked={formValues.textAreaPolicy === 'block_on_detection'}
                  onChange={(e) => handleFormChange('textAreaPolicy', e.target.value)}
                  disabled={saving}
                  className="mt-0.5 w-4 h-4 border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div>
                  <span className="text-sm font-medium text-white">Block on Detection</span>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Block submission entirely when PHI is detected in textarea fields
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Attestation Required Toggle */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formValues.attestationRequired}
                onChange={(e) => handleFormChange('attestationRequired', e.target.checked)}
                disabled={saving}
                className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div>
                <span className="text-sm font-medium text-white">Attestation Required</span>
                <p className="text-xs text-slate-500 mt-0.5">
                  Require users to confirm no PHI before submission
                </p>
              </div>
            </label>
          </div>

          {/* Scan Enabled Toggle */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formValues.scanEnabled}
                onChange={(e) => handleFormChange('scanEnabled', e.target.checked)}
                disabled={saving}
                className="w-5 h-5 rounded border-slate-700 bg-slate-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div>
                <span className="text-sm font-medium text-white">PHI Scanning Enabled</span>
                <p className="text-xs text-slate-500 mt-0.5">
                  Enable runtime PHI detection heuristics (email, phone, MRN, DOB, etc.)
                </p>
              </div>
            </label>
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t border-slate-700/50">
            <button
              onClick={handleSave}
              disabled={saving || loading || !selectedTenantId}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Policy</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* No Tenant Selected State */}
      {!selectedTenantId && !loading && (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-12 text-center">
          <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Please select a tenant to view and edit PHI policy settings.</p>
        </div>
      )}
    </div>
  );
};

export default PhiPolicySettingsView;
